import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, useNavigate, Outlet } from "react-router-dom";
import { animate } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { Flame, Zap, LogOut, Home, Map, Trophy, BarChart3, Palette, ShoppingBag, Medal, Menu, ShieldCheck, FileText, Volume2, VolumeX, Mic, MicOff, Trash2, Bug, Languages, Infinity as InfinityIcon, Calendar, Target, Footprints, User, Gift } from "lucide-react";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "./ui/sheet";
import { Switch } from "./ui/switch";
import { avatarOf, customAvatarSrc } from "../lib/avatars";
import { useTranslation } from "react-i18next";
import { LANGUAGES, setLanguage } from "../i18n";
import { isHealthSupported } from "../lib/health";
import { getCoachGender, setCoachGender } from "../lib/coach";
import { setStepsOptIn, useStepsUiEnabled } from "../lib/stepsOptIn";
import { partnerPerksEnabled } from "../lib/rewards";
import { useOfflineStatus } from "../lib/useOfflineStatus";
import { CloudOff, RefreshCw } from "lucide-react";

export default function Layout() {
  const { t, i18n } = useTranslation();
  const { user, profile, logout } = useAuth();
  const navigate = useNavigate();
  const [moreOpen, setMoreOpen] = useState(false);
  const { online, pending } = useOfflineStatus();

  // Bottom tab bar — primary destinations (iter95 nav refactor).
  const bottomTabs = [
    { to: "/dashboard", label: t("nav.home"), icon: Home, testid: "tab-home" },
    { to: "/journey", label: t("nav.journey"), icon: Map, testid: "tab-journey" },
    { to: "/daily-challenge", label: t("nav.daily_short"), icon: Calendar, testid: "tab-daily" },
    { to: "/weekly-tournament", label: t("nav.weekly_short"), icon: Trophy, testid: "tab-weekly" },
  ];

  const moreLinks = [
    ...(partnerPerksEnabled() ? [{ to: "/rewards", label: t("nav.rewards"), icon: Gift, testid: "more-rewards", desc: t("nav.rewards_desc") }] : []),
    { to: "/store", label: t("nav.store"), icon: ShoppingBag, testid: "more-store", desc: t("nav.store_desc") },
    { to: "/leaderboard", label: t("nav.leaders"), icon: Medal, testid: "more-leaderboard", desc: t("nav.leaders_desc") },
    { to: "/challenges", label: t("nav.challenges"), icon: Target, testid: "more-challenges", desc: t("nav.challenges_desc") },
    { to: "/endurance", label: t("nav.endurance"), icon: InfinityIcon, testid: "more-endurance", desc: t("nav.endurance_desc") },
    { to: "/profile", label: t("nav.profile"), icon: User, testid: "more-profile", desc: t("nav.profile_desc") },
    { to: "/stats", label: t("nav.stats"), icon: BarChart3, testid: "more-stats", desc: t("nav.stats_desc") },
    { to: "/themes", label: t("nav.themes_avatars"), icon: Palette, testid: "more-themes", desc: t("nav.themes_desc") },
    { to: "/privacy", label: t("nav.privacy"), icon: ShieldCheck, testid: "more-privacy", desc: t("nav.privacy_desc") },
    { to: "/terms", label: t("nav.terms"), icon: FileText, testid: "more-terms", desc: t("nav.terms_desc") },
    { to: "/delete-account", label: t("nav.delete_account"), icon: Trash2, testid: "more-delete", desc: t("nav.delete_account_desc") },
  ];

  // Settings live in localStorage so they survive across sessions on the
  // same device. Default ON for both — users can mute via the toggles.
  const [sfxOn, setSfxOn] = useState(
    () => typeof window !== "undefined" ? localStorage.getItem("fitjong_sfx_off") !== "1" : true,
  );
  const [voiceOn, setVoiceOn] = useState(
    () => typeof window !== "undefined" ? localStorage.getItem("fitjong_voice_off") !== "1" : true,
  );
  const toggleSfx = (v) => {
    setSfxOn(v);
    try { localStorage.setItem("fitjong_sfx_off", v ? "0" : "1"); } catch (_) {}
  };
  const toggleVoice = (v) => {
    setVoiceOn(v);
    try { localStorage.setItem("fitjong_voice_off", v ? "0" : "1"); } catch (_) {}
  };
  const [coachGender, setCoachGenderState] = useState(() => getCoachGender());
  const pickCoachGender = (g) => { setCoachGender(g); setCoachGenderState(g); };
  // Iter73 — Steps-sync opt-in toggle. Defaults to "yes" for legacy
  // installs (anyone who has used Health before this shipped) so they
  // keep their refills, and to "no" only when the user explicitly
  // declined in the welcome carousel.
  // Iter76 — Use the shared hook so the Switch instantly reflects
  // changes triggered from anywhere (carousel, settings, devtools).
  const stepsOn = useStepsUiEnabled();
  const toggleSteps = (v) => {
    setStepsOptIn(v ? "yes" : "no");
  };
  const onLogout = async () => {
    setMoreOpen(false);
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-[#F7F5F0] text-[#2C332A] overflow-x-hidden" style={{ fontFamily: "DM Sans, sans-serif" }}>
      <header className="sticky top-0 z-40 bg-[#F7F5F0]/90 backdrop-blur-xl border-b border-[#E6E2D8] safe-top">
        <div className="max-w-7xl mx-auto px-3 md:px-8 h-14 md:h-16 flex items-center justify-between gap-2 md:gap-4">
          {user && profile && (
            <Link
              to="/profile"
              data-testid="hud-avatar-chip"
              className="flex items-center gap-2 shrink min-w-0 active:scale-[0.97] transition-all"
              title={t("nav.edit_avatar")}
            >
              {customAvatarSrc(profile) ? (
                <span
                  data-testid="hud-avatar-photo"
                  className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-cover bg-center shrink-0 hover:opacity-90 ring-2 ring-white/90 shadow-md"
                  style={{ backgroundImage: `url(${customAvatarSrc(profile)})` }}
                  aria-hidden="true"
                />
              ) : profile.selected_avatar ? (
                <span
                  className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-cover bg-center shrink-0 hover:opacity-90 ring-2 ring-white/90 shadow-md"
                  style={{ backgroundImage: `url(${avatarOf(profile).img})` }}
                  aria-hidden="true"
                />
              ) : (
                <span
                  data-testid="hud-avatar-initials"
                  className="w-12 h-12 md:w-14 md:h-14 rounded-full shrink-0 ring-2 ring-white/90 shadow-md flex items-center justify-center bg-gradient-to-br from-[#5F7161] to-[#4D5D4F] text-white text-base md:text-lg font-semibold"
                  style={{ fontFamily: "Outfit, sans-serif" }}
                  aria-hidden="true"
                >
                  {initialsOf(profile.display_name || user.name || user.email)}
                </span>
              )}
              <span
                className="hidden sm:inline truncate text-sm font-semibold max-w-[110px]"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                {(profile.display_name || user.name || user.email || t("hud.player")).split(" ")[0]}
              </span>
            </Link>
          )}

          <div className="flex items-center gap-1 md:gap-3 shrink-0">
            <OfflineChip online={online} pending={pending} t={t} />
            <StatusPill
              testid="hud-energy"
              icon={Zap}
              value={profile ? `${profile.energy}/${profile.max_energy}` : "-"}
              color="#5F7161"
              title={t("hud.energy")}
              info={t("hud.energy_info")}
            />
            <StatusPill
              testid="hud-streak"
              icon={Flame}
              value={profile?.current_streak ?? 0}
              color="#D0A98F"
              title={t("hud.streak")}
              info={t("hud.streak_info")}
            />
            <StatusPill
              testid="hud-coins"
              icon={CoinsStack}
              value={profile?.coins ?? 0}
              color="#E8A365"
              title={t("hud.coins")}
              info={t("hud.coins_info")}
              countUp
            />
          </div>
        </div>
      </header>

      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent
          side="left"
          className="w-[88vw] max-w-sm rounded-r-3xl border-r border-[#E6E2D8] bg-[#F7F5F0] flex flex-col p-0"
          data-testid="menu-sheet"
        >
          <SheetHeader className="px-5 pt-5 pb-3 border-b border-[#E6E2D8] text-left">
            <SheetTitle
              style={{ fontFamily: "Outfit, sans-serif" }}
              className="text-2xl font-light flex items-center gap-3"
            >
              <Menu size={22} strokeWidth={1.6} />
              {t("nav.menu")}
            </SheetTitle>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto p-3">
            {/* Browse group (moved above Settings per user request; Store & Leaders first) */}
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#2C332A]/55 px-3 pt-2 pb-1 font-semibold">
              {t("nav.browse")}
            </p>
            <div className="bg-white rounded-2xl border border-[#E6E2D8] divide-y divide-[#EFEBE0] mb-4">
              {moreLinks.map((it) => (
                <Link
                  key={it.to}
                  to={it.to}
                  data-testid={it.testid}
                  onClick={() => setMoreOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-[#F7F5F0] active:bg-[#EFEBE0]"
                >
                  <it.icon size={18} strokeWidth={1.6} className="text-[#5F7161] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{it.label}</p>
                    <p className="text-xs text-[#2C332A]/60 truncate">{it.desc}</p>
                  </div>
                </Link>
              ))}
            </div>

            {/* Settings group */}
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#2C332A]/55 px-3 pt-1 pb-1 font-semibold">
              {t("nav.settings")}
            </p>
            <div className="bg-white rounded-2xl border border-[#E6E2D8] divide-y divide-[#EFEBE0] mb-4">
              <div className="flex items-center gap-3 px-4 py-3">
                {sfxOn ? <Volume2 size={18} strokeWidth={1.6} className="text-[#5F7161] shrink-0" /> : <VolumeX size={18} strokeWidth={1.6} className="text-[#2C332A]/40 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{t("nav.sound_effects")}</p>
                  <p className="text-xs text-[#2C332A]/60">{t("nav.sound_effects_desc")}</p>
                </div>
                <Switch data-testid="setting-sfx-switch" checked={sfxOn} onCheckedChange={toggleSfx} />
              </div>
              <div className="flex items-center gap-3 px-4 py-3">
                {voiceOn ? <Mic size={18} strokeWidth={1.6} className="text-[#5F7161] shrink-0" /> : <MicOff size={18} strokeWidth={1.6} className="text-[#2C332A]/40 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{t("nav.coach_voice")}</p>
                  <p className="text-xs text-[#2C332A]/60">{t("nav.coach_voice_desc")}</p>
                </div>
                <Switch data-testid="setting-voice-switch" checked={voiceOn} onCheckedChange={toggleVoice} />
              </div>
              {voiceOn && (
                <div className="px-4 py-3" data-testid="setting-coach-gender-row">
                  <div className="flex items-center gap-3">
                    <Mic size={18} strokeWidth={1.6} className="text-[#5F7161] shrink-0 opacity-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{t("nav.coach_voice_type", "Coach voice")}</p>
                      <p className="text-xs text-[#2C332A]/60">{t("nav.coach_voice_type_desc", "Choose your trainer")}</p>
                    </div>
                  </div>
                  <div className="mt-2.5 pl-[30px]">
                    <div className="inline-flex items-center gap-1 rounded-full bg-[#EFF1EC] p-1">
                      <button
                        data-testid="coach-gender-male"
                        onClick={() => pickCoachGender("m")}
                        className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${coachGender === "m" ? "bg-[#5F7161] text-white" : "text-[#5F7161]"}`}
                      >
                        {t("nav.coach_male", "Male")}
                      </button>
                      <button
                        data-testid="coach-gender-female"
                        onClick={() => pickCoachGender("f")}
                        className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${coachGender === "f" ? "bg-[#5F7161] text-white" : "text-[#5F7161]"}`}
                      >
                        {t("nav.coach_female", "Female")}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Iter73 — Steps sync toggle. Only surfaced on Android
                  (Health Connect platform) — testers without a watch /
                  step tracker can hide the Steps energy-refill UI on
                  Home, and flip it back on later from this Settings row
                  if their habits change. */}
              {isHealthSupported() && (
                <div className="flex items-center gap-3 px-4 py-3" data-testid="setting-steps-row">
                  <Footprints size={18} strokeWidth={1.6} className={stepsOn ? "text-[#5F7161] shrink-0" : "text-[#2C332A]/40 shrink-0"} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{t("nav.steps_sync")}</p>
                    <p className="text-xs text-[#2C332A]/60">{t("nav.steps_sync_desc")}</p>
                  </div>
                  <Switch data-testid="setting-steps-switch" checked={stepsOn} onCheckedChange={toggleSteps} />
                </div>
              )}
              {/* Language picker */}
              <div className="px-4 py-3">
                <div className="flex items-center gap-3">
                  <Languages size={18} strokeWidth={1.6} className="text-[#5F7161] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{t("nav.language")}</p>
                    <p className="text-xs text-[#2C332A]/60">{t("nav.language_desc")}</p>
                  </div>
                </div>
                <div className="mt-2.5 pl-[30px] flex items-center gap-2">
                  {LANGUAGES.map((l) => {
                    const active = i18n.language?.startsWith(l.code);
                    return (
                      <button
                        key={l.code}
                        data-testid={`lang-${l.code}`}
                        type="button"
                        onClick={() => setLanguage(l.code)}
                        className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${
                          active
                            ? "bg-[#5F7161] text-white"
                            : "bg-[#F0EDE3] text-[#2C332A]/70 hover:bg-[#E6E2D8]"
                        }`}
                        aria-label={l.label}
                      >
                        <span className="mr-1">{l.flag}</span>{l.code.toUpperCase()}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-[#E6E2D8] mb-4">
              <button
                data-testid="more-logout-btn"
                onClick={onLogout}
                className="flex items-center gap-3 px-4 py-3 w-full text-left hover:bg-[#F7F5F0] active:bg-[#EFEBE0] rounded-2xl"
              >
                <LogOut size={18} strokeWidth={1.6} className="text-[#B5564B] shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#B5564B]">{t("nav.sign_out")}</p>
                  <p className="text-xs text-[#2C332A]/60">{t("nav.sign_out_desc")}</p>
                </div>
              </button>
            </div>

            <p className="text-center text-[10px] text-[#2C332A]/40 mt-2 mb-4">
              {t("nav.footer")}
            </p>
          </div>
        </SheetContent>
      </Sheet>

      <main className="max-w-7xl mx-auto px-3 md:px-8 py-4 md:py-12 pb-24 md:pb-28">
        <Outlet />
      </main>

      {/* Bottom tab bar — iter95 nav refactor. Fixed, safe-area aware. */}
      <nav
        data-testid="bottom-tab-bar"
        className="fixed bottom-0 inset-x-0 z-40 bg-[#F7F5F0]/95 backdrop-blur-xl border-t border-[#E6E2D8] safe-bottom"
      >
        <div className="max-w-md mx-auto px-2 flex items-stretch justify-around">
          {bottomTabs.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={it.testid}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-0.5 flex-1 py-2 min-h-[56px] transition-colors ${
                  isActive ? "text-[#5F7161]" : "text-[#2C332A]/55 hover:text-[#2C332A]/80"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span
                    className={`flex items-center justify-center w-10 h-7 rounded-full transition-colors ${
                      isActive ? "bg-[#5F7161]/12" : "bg-transparent"
                    }`}
                  >
                    <it.icon size={20} strokeWidth={isActive ? 2.2 : 1.8} />
                  </span>
                  <span className="text-[10px] font-semibold tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
                    {it.label}
                  </span>
                </>
              )}
            </NavLink>
          ))}
          <button
            type="button"
            data-testid="tab-more"
            onClick={() => setMoreOpen(true)}
            className={`flex flex-col items-center justify-center gap-0.5 flex-1 py-2 min-h-[56px] transition-colors ${
              moreOpen ? "text-[#5F7161]" : "text-[#2C332A]/55 hover:text-[#2C332A]/80"
            }`}
            aria-label={t("nav.more")}
          >
            <span
              className={`flex items-center justify-center w-10 h-7 rounded-full transition-colors ${
                moreOpen ? "bg-[#5F7161]/12" : "bg-transparent"
              }`}
            >
              <Menu size={20} strokeWidth={moreOpen ? 2.2 : 1.8} />
            </span>
            <span className="text-[10px] font-semibold tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
              {t("nav.more")}
            </span>
          </button>
        </div>
      </nav>
    </div>
  );
}

function OfflineChip({ online, pending, t }) {
  // Offline → amber "Offline" chip (with pending count if any).
  // Online with a backlog → subtle "Syncing N" chip that clears once flushed.
  if (online && pending === 0) return null;
  const offline = !online;
  return (
    <div
      data-testid="offline-status-chip"
      title={offline
        ? t("offline.chip_offline", "You're offline — games are saved and will sync")
        : t("offline.chip_syncing", "Syncing your offline games…")}
      className={`flex items-center gap-1 h-8 md:h-9 px-2 md:px-3 rounded-full border transition-all ${
        offline
          ? "bg-[#FBEEDD] border-[#E8A365]/50 text-[#9A5B1E]"
          : "bg-[#EAF0EA] border-[#5F7161]/40 text-[#4D5D4F]"
      }`}
    >
      {offline ? (
        <CloudOff size={14} strokeWidth={1.6} />
      ) : (
        <RefreshCw size={14} strokeWidth={1.6} className="animate-spin" />
      )}
      <span className="text-xs md:text-sm font-semibold tabular-nums">
        {offline
          ? (pending > 0 ? t("offline.chip_offline_n", { count: pending, defaultValue: "Offline · {{count}}" }) : t("offline.chip_offline_short", "Offline"))
          : pending}
      </span>
    </div>
  );
}

function AnimatedNumber({ value }) {
  const [display, setDisplay] = useState(value);
  const prev = useRef(value);
  useEffect(() => {
    const from = Number(prev.current) || 0;
    const to = Number(value) || 0;
    prev.current = to;
    if (from === to) { setDisplay(to); return; }
    const controls = animate(from, to, {
      duration: 0.9,
      ease: "easeOut",
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    return () => controls.stop();
  }, [value]);
  return <>{display}</>;
}

function StatusPill({ icon: Icon, value, color, testid, title, info, countUp }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          data-testid={testid}
          type="button"
          aria-label={`${title}: ${value}.`}
          className="flex items-center gap-1 h-8 md:h-9 px-2 md:px-3 rounded-full bg-white border border-[#E6E2D8] hover:bg-[#F0EDE3] active:scale-[0.96] transition-all cursor-pointer"
        >
          <Icon size={14} strokeWidth={1.5} style={{ color }} />
          <span className="text-xs md:text-sm font-medium tabular-nums">
            {countUp ? <AnimatedNumber value={value} /> : value}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        data-testid={`${testid}-info`}
        side="bottom"
        align="end"
        className="w-64 rounded-2xl border-[#E6E2D8] bg-[#F7F5F0] shadow-xl"
      >
        <div className="flex items-start gap-3">
          <div
            className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center"
            style={{ backgroundColor: `${color}20` }}
          >
            <Icon size={18} strokeWidth={1.75} style={{ color }} />
          </div>
          <div className="min-w-0">
            <p
              style={{ fontFamily: "Outfit, sans-serif" }}
              className="text-base font-medium tracking-tight text-[#2C332A]"
            >
              {title}
            </p>
            <p className="text-xs text-[#2C332A]/70 leading-relaxed mt-1">
              {info}
            </p>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Stacked-coins icon — three vertically stacked discs with subtle highlights.
function CoinsStack({ size = 14, color = "currentColor", strokeWidth = 1.5 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {/* Bottom coin */}
      <ellipse cx="12" cy="18" rx="8" ry="2.2" fill={color} fillOpacity="0.18" />
      <ellipse cx="12" cy="18" rx="8" ry="2.2" />
      {/* Middle coin */}
      <ellipse cx="12" cy="13" rx="8" ry="2.2" fill={color} fillOpacity="0.18" />
      <ellipse cx="12" cy="13" rx="8" ry="2.2" />
      <line x1="4" y1="13" x2="4" y2="18" />
      <line x1="20" y1="13" x2="20" y2="18" />
      {/* Top coin */}
      <ellipse cx="12" cy="8" rx="8" ry="2.2" fill={color} fillOpacity="0.30" />
      <ellipse cx="12" cy="8" rx="8" ry="2.2" />
      <line x1="4" y1="8" x2="4" y2="13" />
      <line x1="20" y1="8" x2="20" y2="13" />
      {/* Top sheen */}
      <path d="M7 7 Q12 5.6 17 7" strokeOpacity="0.8" />
    </svg>
  );
}

// Initials helper for the "no avatar yet" placeholder. Pulls up to two
// letters from the user's display name (or email local-part), uppercased.
// Used by the HUD avatar chip when `profile.selected_avatar` is null.
function initialsOf(nameOrEmail) {
  const raw = (nameOrEmail || "").trim();
  if (!raw) return "?";
  const local = raw.includes("@") ? raw.split("@")[0] : raw;
  const tokens = local.split(/[\s._-]+/).filter(Boolean);
  if (tokens.length === 0) return raw[0].toUpperCase();
  if (tokens.length === 1) return tokens[0].slice(0, 2).toUpperCase();
  return (tokens[0][0] + tokens[tokens.length - 1][0]).toUpperCase();
}

